import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PiggyBank, Search } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import nurseryAccountsService from '../../services/nurseryAccounts';
import { errorMessage } from '../../utils/apiResponse';
import { money } from '../../utils/nurseryMoney';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { CustomerDeposit, DepositsOverview, DepositToRequest, FoundInXero } from '../../types/nurseryAccounts';

/**
 * /accounts/deposits — customer deposits: orders still to be asked for one,
 * what each paying customer holds, and every deposit invoice.
 *
 * Paid only ever comes from Xero, once a deposit is reconciled there (Rob,
 * 2026-10-14): nothing on this page records a payment. A deposit from before
 * (an opening balance from MYOB) is entered in Xero as a paid invoice to the
 * Customer Deposits account and taken on here with "Find deposits in Xero".
 * Any accounts seat asks for a deposit; finding and voiding is the Accounts
 * Admin's. The server works everything out.
 */

const ymd = (d: Date) => d.toISOString().slice(0, 10);

function stateOf(d: CustomerDeposit): { text: string; tone: string } {
  if (d.status === 'void') return { text: 'Void', tone: 'bg-neutral-100 text-neutral-600' };
  if (d.status === 'paid') {
    return d.available > 0
      ? { text: 'Paid, held', tone: 'bg-green-100 text-green-800' }
      : { text: 'Paid, used', tone: 'bg-neutral-100 text-neutral-700' };
  }
  if (d.xero_sync_status === 'failed') return { text: 'Not sent to Xero', tone: 'bg-red-100 text-red-800' };
  if (!d.xero_invoice_id) return { text: 'On its way to Xero', tone: 'bg-blue-100 text-blue-800' };
  if (d.amount_paid > 0) return { text: 'Part paid', tone: 'bg-amber-100 text-amber-800' };
  return { text: 'Waiting for payment', tone: 'bg-amber-100 text-amber-800' };
}

function AskRow({ row, xeroActive, onDone }: { row: DepositToRequest; xeroActive: boolean; onDone: () => void }) {
  const [amount, setAmount] = useState(row.suggested.toFixed(2));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ask = async () => {
    const value = Math.round(Number(amount.replace(/[$,\s]/g, '')) * 100) / 100;
    if (!(value > 0)) { setError('Say how much the deposit is.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.requestDeposit(row.order_id, { amount: value });
      if (res.success) onDone();
      else setError(errorMessage(res, 'The deposit could not be asked for'));
    } catch (e) {
      setError(errorMessage(e, 'The deposit could not be asked for'));
    }
    setBusy(false);
  };
  return (
    <li className="py-3 space-y-2 min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 min-w-0">
        <div className="min-w-0">
          <Link to={`/nursery/orders/${row.order_id}`} className="font-medium text-primary-700 hover:underline break-all">{row.order_number}</Link>
          <span className="text-neutral-700 break-words"> · {row.customer_name}</span>
          {row.payer_name !== row.customer_name && <span className="text-neutral-500 break-words"> (paid by {row.payer_name})</span>}
        </div>
        <span className="text-sm text-neutral-600">{row.pct}% of {money(row.basis)} = {money(row.suggested)}</span>
      </div>
      {row.unpriced > 0 && (
        <p className="text-xs text-amber-700">{row.unpriced} line{row.unpriced === 1 ? ' has' : 's have'} no price and {row.unpriced === 1 ? 'is' : 'are'} left out.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`ask-${row.order_id}`} className="sr-only">Deposit for {row.order_number}</label>
        <input id={`ask-${row.order_id}`} type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
          className="w-32 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm" />
        <Button size="sm" onClick={ask} disabled={busy || !xeroActive}>{busy ? 'Asking…' : 'Ask for deposit'}</Button>
        <Link to={`/nursery/orders/${row.order_id}`} className="text-sm text-primary-700 hover:underline">Or say none is needed</Link>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </li>
  );
}

function FindInXero({ onFound }: { onFound: () => void }) {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return ymd(d);
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FoundInXero | null>(null);
  const find = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.findDepositsInXero(from);
      if (res.success && res.data) { setResult(res.data); onFound(); }
      else setError(errorMessage(res, 'Xero could not be searched'));
    } catch (e) {
      setError(errorMessage(e, 'Xero could not be searched'));
    }
    setBusy(false);
  };
  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
      <h2 className="text-base font-semibold text-neutral-900">Deposits already in Xero</h2>
      <p className="text-sm text-neutral-600 break-words">
        A deposit taken before (an opening balance from MYOB) is entered in Xero, not here: an invoice to the
        customer&apos;s Xero contact, coded to the Customer Deposits account and BAS Excluded, with a payment recorded
        against it in Xero. One way to do it: journal the opening Customer Deposits balance to a clearing account that
        takes payments, then raise each customer&apos;s deposit invoice and pay it from that clearing account. Check it
        with your accountant. Then find them here: each is held for its customer and taken off their invoices like any
        other deposit.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <label htmlFor="find-from" className="block text-sm font-medium text-neutral-800">Invoices dated from</label>
          <input id="find-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-2 text-sm" />
        </div>
        <Button onClick={find} disabled={busy || !from}>
          <Search className="h-4 w-4 mr-1" /> {busy ? 'Searching Xero…' : 'Find deposits in Xero'}
        </Button>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      {result && (
        <div className="text-sm space-y-2 break-words">
          <p className="text-neutral-800">
            Found {result.found} deposit invoice{result.found === 1 ? '' : 's'} in Xero: {result.added.length} taken on now,
            {' '}{result.already} already here.
          </p>
          {result.unmatched.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900 space-y-1">
              <p>
                These belong to Xero contacts no customer is linked to. Link the contact on the customer&apos;s Account tab
                (Xero contact), then find again:
              </p>
              <ul className="list-disc pl-5">
                {result.unmatched.map((u) => (
                  <li key={`${u.number}-${u.contact_name}`}>{u.contact_name || 'No name'}: {u.number || 'no number'}, {money(u.amount)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Deposits() {
  const tz = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const payerFilter = params.get('payer') ? Number(params.get('payer')) : null;
  const [data, setData] = useState<DepositsOverview | null>(null);
  const [showVoid, setShowVoid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryAccountsService.getDeposits({ include_void: showVoid });
      if (res.success && res.data) setData(res.data);
      else setError(errorMessage(res, 'The deposits could not be read'));
    } catch (e) {
      setError(errorMessage(e, 'The deposits could not be read'));
    }
  }, [showVoid]);

  useEffect(() => { load(); }, [load]);

  const deposits = useMemo(
    () => (data?.deposits || []).filter((d) => !payerFilter || d.payer_customer_id === payerFilter),
    [data, payerFilter],
  );
  const payers = useMemo(
    () => (data?.payers || []).filter((p) => !payerFilter || p.payer_id === payerFilter),
    [data, payerFilter],
  );
  const toRequest = useMemo(
    () => (data?.to_request || []).filter((r) => !payerFilter || r.payer_id === payerFilter),
    [data, payerFilter],
  );
  const filterName = payerFilter ? (data?.payers.find((p) => p.payer_id === payerFilter)?.payer_name
    ?? data?.deposits.find((d) => d.payer_customer_id === payerFilter)?.payer_name) : null;

  const act = async (id: number, fn: () => Promise<{ success: boolean }>, fallback: string) => {
    setBusyId(id);
    setError(null);
    try {
      const res = await fn();
      if (!res.success) setError(errorMessage(res, fallback));
      await load();
    } catch (e) {
      setError(errorMessage(e, fallback));
    }
    setBusyId(null);
  };

  const voidOne = (d: CustomerDeposit) => {
    const reason = window.prompt(`Why is ${d.deposit_number || 'this deposit'} void?`);
    if (!reason || reason.trim().length < 4) return;
    act(d.id, () => nurseryAccountsService.voidDeposit(d.id, reason.trim()), 'The deposit could not be voided');
  };

  return (
    <>
      <PageHeader
        title="Deposits"
        subtitle="Deposits asked for, held for each customer, and taken off their invoices"
        icon={<PiggyBank className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm text-neutral-600 break-words">
          A deposit counts as paid only once it is reconciled in Xero; nothing here records a payment. Each invoice for a
          customer takes off the deposits they have paid, oldest first, as a GST-free line to Customer Deposits.
        </p>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {data && !data.xero_active && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words">
            Xero is not connected for invoices here, so no deposit can be asked for or paid. <Link to="/accounts/xero" className="underline">Connect Xero</Link>.
          </div>
        )}
        {payerFilter && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-neutral-700 break-words">Showing {filterName || 'one customer'}.</span>
            <button type="button" className="text-primary-700 hover:underline" onClick={() => setParams({})}>Show every customer</button>
          </div>
        )}

        {data && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
            <h2 className="text-base font-semibold text-neutral-900">Orders to ask for a deposit</h2>
            {toRequest.length === 0
              ? <p className="text-sm text-neutral-500 mt-2">None. Orders for customers who pay deposits show here once confirmed, while their trees are not ready.</p>
              : (
                <ul className="divide-y divide-neutral-100">
                  {toRequest.map((r) => <AskRow key={r.order_id} row={r} xeroActive={data.xero_active} onDone={load} />)}
                </ul>
              )}
          </section>
        )}

        {data && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0">
            <h2 className="text-base font-semibold text-neutral-900 mb-2">Held for each customer</h2>
            {payers.length === 0 ? <p className="text-sm text-neutral-500">No deposits yet.</p> : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-500">
                      <th className="py-1 pr-3 font-medium">Customer</th>
                      <th className="py-1 pr-3 font-medium text-right">Paid</th>
                      <th className="py-1 pr-3 font-medium text-right">Taken off invoices</th>
                      <th className="py-1 pr-3 font-medium text-right">Held</th>
                      <th className="py-1 font-medium text-right">Asked, not paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payers.map((p) => (
                      <tr key={p.payer_id} className="border-t border-neutral-100">
                        <td className="py-1.5 pr-3">
                          <button type="button" className="text-primary-700 hover:underline text-left" onClick={() => setParams({ payer: String(p.payer_id) })}>
                            {p.payer_name}
                          </button>
                        </td>
                        <td className="py-1.5 pr-3 text-right">{money(p.paid)}</td>
                        <td className="py-1.5 pr-3 text-right">{money(p.drawn)}</td>
                        <td className="py-1.5 pr-3 text-right font-medium">{money(p.available)}</td>
                        <td className="py-1.5 text-right">{money(p.outstanding)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {data && (
          <section className="bg-white border border-neutral-200 rounded-xl p-4 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-neutral-900">Deposits</h2>
              <label htmlFor="show-void" className="flex items-center gap-2 text-sm text-neutral-700">
                <input id="show-void" type="checkbox" checked={showVoid} onChange={(e) => setShowVoid(e.target.checked)} />
                Show void
              </label>
            </div>
            {deposits.length === 0 ? <p className="text-sm text-neutral-500">None.</p> : (
              <ul className="divide-y divide-neutral-100">
                {deposits.map((d) => {
                  const s = stateOf(d);
                  return (
                    <li key={d.id} className="py-3 space-y-1 min-w-0">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 min-w-0">
                        <div className="min-w-0 break-words">
                          <span className="font-medium text-neutral-900 break-all">{d.deposit_number || 'Deposit'}</span>
                          <span className="text-neutral-700"> · {d.payer_name}</span>
                          {d.order_id && d.order_number && (
                            <> · <Link to={`/nursery/orders/${d.order_id}`} className="text-primary-700 hover:underline">{d.order_number}</Link></>
                          )}
                          {d.source === 'xero' && <span className="text-neutral-500"> · found in Xero</span>}
                        </div>
                        <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${s.tone}`}>{s.text}</span>
                      </div>
                      <p className="text-sm text-neutral-600 break-words">
                        {formatLocalDateInTz(String(d.deposit_date).slice(0, 10), tz)} · {money(d.amount)}
                        {d.status !== 'void' && <> · paid {money(d.amount_paid)} · taken off {money(d.drawn)} · held {money(d.available)}</>}
                      </p>
                      {d.status === 'void' && d.void_reason && <p className="text-sm text-neutral-500 break-words">{d.void_reason}</p>}
                      {d.xero_sync_status === 'failed' && d.xero_sync_error && (
                        <p className="text-sm text-red-700 break-words">{d.xero_sync_error}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {d.xero_sync_status === 'failed' && (
                          <Button size="sm" variant="secondary" disabled={busyId === d.id}
                            onClick={() => act(d.id, () => nurseryAccountsService.retryDepositXero(d.id), 'It could not be sent again')}>
                            Send to Xero again
                          </Button>
                        )}
                        {data.can_admin && d.source === 'request' && d.status === 'open' && d.amount_paid === 0 && (
                          <Button size="sm" variant="secondary" disabled={busyId === d.id} onClick={() => voidOne(d)}>Void</Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {data?.can_admin && <FindInXero onFound={load} />}
      </div>
    </>
  );
}

export default Deposits;
